import React, { useEffect, useState } from "react";
import MultiImageInput from "@/components/MultiImageInput";
import LocationPinPicker from "@/components/maps/LocationPinPicker";
import { MicRow, appendText } from "@/components/VoiceMic";
import { vendorGetShop, vendorUpdateShop } from "@/lib/vendorApi";
import { vendorUpdateDelivery } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { MapPin, Save, Truck, Package, Plus, Trash2, Store, Phone, Clock, Image as ImageIcon } from "lucide-react";
import ImageInput from "@/components/ImageInput";
import AvailabilityToggle from "@/components/AvailabilityToggle";
import PinDistrictHint from "@/components/vendor/PinDistrictHint";

const DELIVERY_RADIUS_OPTIONS = [2, 5, 10, 20, 50];

const FIELDS = [
  { key: "name", label: "Shop name" },
  { key: "tagline", label: "Tagline" },
  { key: "description", label: "Description", textarea: true },
  { key: "phone", label: "Phone" },
  { key: "address", label: "Full address", textarea: true },
  { key: "landmark", label: "Landmark (optional)" },
  { key: "pincode", label: "Pincode" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "hours_label", label: "Shop timing (poster text, optional)", maxLength: 60, placeholder: "e.g. 9 AM – 9 PM, Sunday closed", help: "Shown as text on your QR posters only. Live Open/Closed is still controlled by the shop status switch." },
];

const SECTION = "rounded-2xl border border-slate-200/80 bg-[rgb(var(--vb-cream))]/60 p-4 sm:p-5";
const INPUT = "w-full mt-1 bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition-shadow";
const ICON_WRAP = "w-8 h-8 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0";
const ICON_WRAP_AMBER = "w-8 h-8 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0";
const FIELD_GROUPS = [
  { id: "basic", title: "Basic info", hint: "Name and story customers see first.", icon: Store, keys: ["name", "tagline", "description"] },
  { id: "contact", title: "Contact & address", hint: "Phone, address and PIN for your shop.", icon: Phone, keys: ["phone", "address", "landmark", "pincode", "city", "state"] },
  { id: "poster", title: "Poster timing", hint: "Optional text for QR posters.", icon: Clock, keys: ["hours_label"] },
];
const SectionHead = ({ icon: Icon, title, hint }) => (
  <div className="flex items-center gap-2.5 mb-4">
    <span className={ICON_WRAP}><Icon className="w-4 h-4" /></span>
    <div>
      <h3 className="font-display text-lg font-bold leading-tight">{title}</h3>
      {hint && <p className="text-xs text-slate-500">{hint}</p>}
    </div>
  </div>
);

export default function ShopProfileEditor({ shopId, onSaved }) {
  const { formatApiError } = useApp();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!shopId) return;
    vendorGetShop(shopId).then(setData).catch(() => {});
  }, [shopId]);

  if (!data) return <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center text-slate-500">Loading...</div>;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      for (const f of FIELDS) patch[f.key] = (data[f.key] || "").toString().trim() ? data[f.key].toString().trim() : "";
      patch.latitude = data.latitude ? parseFloat(data.latitude) : null;
      patch.clear_location = data.latitude == null && data.longitude == null;
      patch.longitude = data.longitude ? parseFloat(data.longitude) : null;
      patch.image = data.image;
      patch.images = data.images?.length ? data.images : data.image ? [data.image] : [];
      patch.image_captions = data.image_captions || {};
      patch.logo = data.logo || "";
      patch.delivery_radius_enabled = !!data.delivery_radius_enabled;
      patch.delivery_radius_km = data.delivery_radius_enabled ? (parseFloat(data.delivery_radius_km) || 5) : null;
      await vendorUpdateShop(shopId, patch);
      // Persist today-delivery + slabs via the dedicated delivery endpoint.
      const slabs = (data.delivery_slabs || [])
        .map((s) => ({ km: parseFloat(s.km), price: parseFloat(s.price) }))
        .filter((s) => Number.isFinite(s.km) && s.km > 0 && Number.isFinite(s.price) && s.price >= 0);
      await vendorUpdateDelivery(shopId, {
        delivery_today_available: data.delivery_today_available !== false,
        delivery_slabs: slabs,
      });
      toast.success("Shop profile saved");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f) => (
          <div key={f.key} className={f.textarea ? "sm:col-span-2" : ""}>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{f.label}</label>
            {f.textarea ? (
              <textarea
                value={data[f.key] || ""}
                onChange={(e) => update(f.key, e.target.value)}
                rows={2}
                className={INPUT}
                data-testid={`shop-field-${f.key}`}
              />
            ) : null}
            {f.textarea && <MicRow onText={(t) => update(f.key, appendText(data[f.key] || "", t))} testid={`mic-shop-${f.key}`} />}
            {f.textarea ? null : (
              <input
                value={data[f.key] || ""}
                onChange={(e) => update(f.key, f.maxLength ? e.target.value.slice(0, f.maxLength) : e.target.value)}
                maxLength={f.maxLength}
                placeholder={f.placeholder}
                className={INPUT}
                data-testid={`shop-field-${f.key}`}
              />
            )}
            {f.help && <p className="text-[11px] text-slate-400 mt-1">{f.help}</p>}
          </div>
  );

  return (
    <div className="bg-white border border-slate-200/80 rounded-3xl p-4 sm:p-7 shadow-sm space-y-5" data-testid="shop-profile-editor">
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-2xl bg-emerald-700 text-white flex items-center justify-center shadow-sm shadow-emerald-700/25 flex-shrink-0"><Store className="w-5 h-5" /></div>
        <div>
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight">Shop Profile</h2>
          <p className="text-sm text-slate-500">This information is shown on your public shop page.</p>
        </div>
      </div>

      {FIELD_GROUPS.map((g) => (
        <section key={g.id} className={SECTION} data-testid={`shop-group-${g.id}`}>
          <SectionHead icon={g.icon} title={g.title} hint={g.hint} />
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.filter((f) => g.keys.includes(f.key)).map(renderField).flatMap((el) => el.key === "state"
              ? [el, <PinDistrictHint key="pin-district" pincode={(data.pincode || "").trim()} city={data.city} state={data.state} onUse={(c, s) => { update("city", c); update("state", s); }} />]
              : [el])}
          </div>
        </section>
      ))}

      <div className={SECTION} data-testid="shop-location-section">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><span className={ICON_WRAP}><MapPin className="w-4 h-4" /></span> Precise location</h3>
            <p className="text-xs text-slate-500 mt-0.5">Required to enable Get Directions for customers.</p>
          </div>
        </div>
        <LocationPinPicker lat={data.latitude} lng={data.longitude} onChange={(a, b) => { update("latitude", a); update("longitude", b); }} onClear={() => { update("latitude", null); update("longitude", null); }} testid="shop-pin-picker" />
      </div>

      <div className={SECTION} data-testid="shop-image-section">
        <SectionHead icon={ImageIcon} title="Photos & logo" hint="Cover photo and square logo for your shop page." />
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            <MultiImageInput
              label="Shop photos (first = cover)"
              value={data.images?.length ? data.images : data.image ? [data.image] : []}
              onChange={(list) => { update("images", list); update("image", list[0] || ""); }}
              captions={data.image_captions || {}}
              onCaptionsChange={(c) => update("image_captions", c)}
              testid="shop-image"
              libraryCategory="shops"
              librarySuggest={data.categories}
              disabled={saving}
            />
            <p className="text-[11px] text-slate-500 mt-1">Appears on your public profile cover and share card. Camera, Gallery or paste an image URL.</p>
          </div>
          <div data-testid="shop-logo-section">
            <ImageInput
              label="Shop logo (square)"
              value={data.logo || ""}
              onChange={(url) => update("logo", url)}
              testid="shop-logo"
              disabled={saving}
            />
            <p className="text-[11px] text-slate-500 mt-1">Shown in the small square badge on your public shop page. A square image works best.</p>
          </div>
        </div>
      </div>

      <div className={SECTION} data-testid="delivery-area-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><span className={ICON_WRAP_AMBER}><Truck className="w-4 h-4" /></span> Delivery area</h3>
            <p className="text-xs text-slate-500 mt-0.5">If enabled, customers farther than your chosen radius won't see your shop in nearby searches.</p>
          </div>
          <button
            type="button"
            onClick={() => update("delivery_radius_enabled", !data.delivery_radius_enabled)}
            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${data.delivery_radius_enabled ? "bg-emerald-600" : "bg-slate-300"}`}
            aria-label="Toggle delivery radius"
            data-testid="delivery-radius-toggle"
          >
            <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${data.delivery_radius_enabled ? "translate-x-5" : "translate-x-0"}`} />
          </button>
        </div>
        {data.delivery_radius_enabled && (
          <div className="mt-4" data-testid="delivery-radius-picker">
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">I only deliver within</label>
            <div className="mt-2 inline-flex flex-wrap gap-1 bg-slate-50 border border-slate-200 rounded-full p-1">
              {DELIVERY_RADIUS_OPTIONS.map((km) => {
                const active = Number(data.delivery_radius_km) === km;
                return (
                  <button
                    key={km}
                    type="button"
                    onClick={() => update("delivery_radius_km", km)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${active ? "bg-emerald-700 text-white shadow-sm" : "text-slate-600 hover:bg-white"}`}
                    data-testid={`delivery-radius-${km}`}
                  >
                    {km} km
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className={SECTION} data-testid="today-delivery-section">
        <AvailabilityToggle
          active={data.delivery_today_available !== false}
          onToggle={() => update("delivery_today_available", data.delivery_today_available === false)}
          eyebrow="Today Delivery Service Available"
          activeTitle="Yes — delivering today"
          inactiveTitle="No — not delivering today"
          activeHint="customers in your area can order"
          inactiveHint="orders paused until you turn it on"
          ariaLabel="Toggle today's delivery availability"
          testid="today-delivery-toggle"
        />
        <p className="text-[11px] text-slate-500 mt-1">Flip this to pause/resume today's delivery without changing your shop's Open/Closed status.</p>
      </div>

      <div className={SECTION} data-testid="delivery-slabs-section">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
          <div>
            <h3 className="font-display text-lg font-bold flex items-center gap-2"><span className={ICON_WRAP_AMBER}><Package className="w-4 h-4" /></span> Delivery charges by distance</h3>
            <p className="text-xs text-slate-500 mt-0.5">Add slabs so the right charge is applied based on how far the customer is.</p>
          </div>
          <button type="button" onClick={() => update("delivery_slabs", [...(data.delivery_slabs || []), { km: "", price: "" }])} className="inline-flex items-center gap-1 rounded-full border border-emerald-200 text-emerald-800 px-3 py-1.5 text-xs font-semibold hover:bg-emerald-50" data-testid="add-slab-btn">
            <Plus className="w-3.5 h-3.5" /> Add slab
          </button>
        </div>
        <div className="space-y-2">
          {(data.delivery_slabs || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No slabs defined. Default: no delivery charge.</div>
          )}
          {(data.delivery_slabs || []).map((slab, idx) => (
            <div key={idx} className="flex items-center gap-2 flex-wrap" data-testid={`slab-${idx}`}>
              <span className="text-xs text-slate-500">Up to</span>
              <input type="number" step="0.5" min="0.5" value={slab.km ?? ""} onChange={(e) => {
                const next = [...(data.delivery_slabs || [])]; next[idx] = { ...next[idx], km: e.target.value }; update("delivery_slabs", next);
              }} className="w-20 border border-slate-200 rounded-xl px-2 py-1.5 text-sm" data-testid={`slab-km-${idx}`} />
              <span className="text-xs text-slate-500">km →</span>
              <span className="text-xs text-slate-500">₹</span>
              <input type="number" step="1" min="0" value={slab.price ?? ""} onChange={(e) => {
                const next = [...(data.delivery_slabs || [])]; next[idx] = { ...next[idx], price: e.target.value }; update("delivery_slabs", next);
              }} className="w-24 border border-slate-200 rounded-xl px-2 py-1.5 text-sm" data-testid={`slab-price-${idx}`} />
              <button type="button" onClick={() => {
                const next = (data.delivery_slabs || []).filter((_, i) => i !== idx); update("delivery_slabs", next);
              }} aria-label="Remove slab" className="ml-auto p-1.5 text-rose-500 hover:bg-rose-50 rounded-full" data-testid={`slab-remove-${idx}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="sticky bottom-3 z-10 mr-16 sm:mr-0 flex justify-end rounded-2xl bg-white/90 backdrop-blur-md border border-slate-200/80 shadow-lg shadow-slate-900/5 p-2.5">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-full px-6 py-2.5 text-sm font-semibold shadow-sm shadow-emerald-700/25 transition-colors w-full sm:w-auto justify-center" data-testid="save-shop-profile">
          <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  );
}
